import { hasIndexedDb, indexedDbAdapter, resetIndexedDbOfflineStoreForTests } from './offline-store-indexeddb';
import {
  DEFAULT_VISIBLE_QUEUE_STATUSES,
  OFFLINE_CHANGE_EVENT,
  createQueueRecord,
  emitOfflineChange,
  nowIso,
  type OfflineCacheRecord,
  type OfflineQueueRecord,
  type OfflineQueuedOperationStatus,
  type OfflineStoreAdapter,
  type QueueOperationInput,
} from './offline-store-types';

export type {
  OfflineCacheRecord,
  OfflineQueueRecord,
  OfflineQueuedOperationStatus,
  QueueOperationInput,
} from './offline-store-types';

let memoryCache = new Map<string, OfflineCacheRecord>();
let memoryQueue = new Map<string, OfflineQueueRecord>();
let forceMemoryStore = false;

const memoryAdapter: OfflineStoreAdapter = {
  getCache<T>(key: string) {
    return Promise.resolve((memoryCache.get(key) as OfflineCacheRecord<T> | undefined) ?? null);
  },

  setCache<T>(record: OfflineCacheRecord<T>) {
    memoryCache.set(record.key, record as OfflineCacheRecord);
    emitOfflineChange();
    return Promise.resolve();
  },

  listQueuedOperations(statuses = DEFAULT_VISIBLE_QUEUE_STATUSES) {
    const records = Array.from(memoryQueue.values())
      .filter((record) => statuses.includes(record.status))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return Promise.resolve(records);
  },

  queueOperation(input: QueueOperationInput) {
    const record = createQueueRecord(input);
    memoryQueue.set(record.id, record);
    emitOfflineChange();
    return Promise.resolve(record);
  },

  updateOperation(id: string, patch: Partial<OfflineQueueRecord>) {
    const current = memoryQueue.get(id);
    if (current) {
      memoryQueue.set(id, { ...current, ...patch, updatedAt: nowIso() });
    }
    emitOfflineChange();
    return Promise.resolve();
  },

  removeOperation(id: string) {
    memoryQueue.delete(id);
    emitOfflineChange();
    return Promise.resolve();
  },
};

function adapter(): OfflineStoreAdapter {
  if (forceMemoryStore) return memoryAdapter;
  return hasIndexedDb() ? indexedDbAdapter : memoryAdapter;
}

export function offlineCacheKey(parts: readonly unknown[]): string {
  return parts.map((part) => JSON.stringify(part)).join(':');
}

export async function getOfflineCache<T>(key: string): Promise<OfflineCacheRecord<T> | null> {
  return adapter().getCache<T>(key);
}

export async function setOfflineCache<T>(
  key: string,
  data: T,
  options: { projectId?: string | null; routeId?: string | null } = {}
): Promise<void> {
  await adapter().setCache({
    key,
    data,
    projectId: options.projectId ?? null,
    routeId: options.routeId ?? null,
    updatedAt: nowIso(),
  });
}

export async function queueOfflineOperation(input: QueueOperationInput): Promise<OfflineQueueRecord> {
  return adapter().queueOperation(input);
}

export async function listOfflineQueuedOperations(
  statuses?: readonly OfflineQueuedOperationStatus[]
): Promise<OfflineQueueRecord[]> {
  return adapter().listQueuedOperations(statuses);
}

export const updateOfflineOperation = (id: string, patch: Partial<OfflineQueueRecord>): Promise<void> =>
  adapter().updateOperation(id, patch);

export async function getOfflinePendingCount(): Promise<number> {
  return (await listOfflineQueuedOperations()).length;
}

export async function getOfflineQueueCounts(): Promise<
  Record<'queued' | 'syncing' | 'failed' | 'conflict', number>
> {
  const records = await listOfflineQueuedOperations(['queued', 'syncing', 'failed', 'conflict']);
  return records.reduce(
    (counts, record) => {
      if (
        record.status === 'queued' ||
        record.status === 'syncing' ||
        record.status === 'failed' ||
        record.status === 'conflict'
      ) {
        counts[record.status] += 1;
      }
      return counts;
    },
    { queued: 0, syncing: 0, failed: 0, conflict: 0 }
  );
}

export async function markOfflineOperationSynced(id: string): Promise<void> {
  await updateOfflineOperation(id, { status: 'synced', lastError: null, nextRetryAt: null });
}

export async function markOfflineOperationSyncing(id: string): Promise<void> {
  await updateOfflineOperation(id, { status: 'syncing', lastError: null, nextRetryAt: null });
}

export async function markOfflineOperationFailed(id: string, error: string): Promise<void> {
  const [operation] = (await listOfflineQueuedOperations(['queued', 'syncing', 'failed'])).filter(
    (record) => record.id === id
  );
  const retryCount = operation ? operation.retryCount + 1 : 1;
  const delaySeconds = Math.min(300, 2 ** Math.max(0, retryCount - 1) * 10);
  await updateOfflineOperation(id, {
    status: 'failed',
    lastError: error,
    retryCount,
    nextRetryAt: new Date(Date.now() + delaySeconds * 1000).toISOString(),
  });
}

export async function markOfflineOperationConflict(id: string, error: string): Promise<void> {
  await updateOfflineOperation(id, { status: 'conflict', lastError: error, nextRetryAt: null });
}

export async function removeOfflineOperation(id: string): Promise<void> {
  await adapter().removeOperation(id);
}

export function subscribeToOfflineStore(callback: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  window.addEventListener(OFFLINE_CHANGE_EVENT, callback);
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);

  return () => {
    window.removeEventListener(OFFLINE_CHANGE_EVENT, callback);
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}

export function resetOfflineMemoryStoreForTests(): void {
  memoryCache = new Map();
  memoryQueue = new Map();
}

export function useOfflineMemoryStoreForTests(enabled: boolean): void {
  forceMemoryStore = enabled;
  resetIndexedDbOfflineStoreForTests();
}
